import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { gzLog, gzLogProps } from "../../utils/gzAnalytics";

const logsContext = createContext<LogsReturnProps | null>(null);
const { Provider } = logsContext;

type Props = {
  children: React.ReactNode;
  targetSequence: string[];
};

function isDevMode() {
  const searchParams = new URLSearchParams(window.location.search);
  const showLogs = searchParams.get("logs")
  if (showLogs === "true") return true;
  if (showLogs === "false") return false;
  return import.meta.env.VITE_ENVIRONMENT === "staging";
}

export function LogsProvider({ children, targetSequence }: Props) {
  const [pressedKeys, setPressedKeys] = useState<string[]>([]);
  const [logs, setLogs] = useState<string[]>([]);

  const [showLogs, setShowLogs] = useState(isDevMode);

  // Instead of using Next.js router, we can use URLSearchParams for query parameters
  const urlParams = new URLSearchParams(window.location.search);
  const hideLogs = urlParams.get("hideLogs");

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      setPressedKeys((prevKeys) => [...prevKeys, e.key]);

      if (pressedKeys.length > targetSequence.length) {
        setPressedKeys((prevKeys) => prevKeys.slice(1));
      }

      const _pressed = [...pressedKeys, e.key];

      if (JSON.stringify(_pressed) === JSON.stringify(targetSequence)) {
        setShowLogs((e) => !e);
        setPressedKeys([]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [pressedKeys, targetSequence]);

  function log(props: gzLogProps) {
    setLogs((e) => [...e, JSON.stringify(props)]);
    gzLog(props);
  }

  return (
    <Provider
      value={{
        showLogs: showLogs && !(hideLogs === "true"),
        logs,
        setLogs,
        log,
      }}
    >
      {children}
    </Provider>
  );
}

export const useLogs: () => LogsReturnProps = () => {
  const context = useContext(logsContext);
  if (!context) throw "useLogs must be used within a LogsProvider";
  return context;
};

export interface LogsReturnProps {
  showLogs: boolean;
  logs: string[];
  setLogs: Dispatch<SetStateAction<string[]>>;
  log: (props: gzLogProps) => void;
}
